/**
 * Event Types - Canonical event name constants for consistency
 * 
 * Event Taxonomy Migration Audit Trail:
 * The following event names were migrated from scattered string literals to centralized constants:
 * 
 * OLD EVENT NAME → NEW CANONICAL NAME (EVENT_TYPES constant)
 * - audio_play → playback_play (PLAYBACK_PLAY)
 * - audio_pause → playback_pause (PLAYBACK_PAUSE)  
 * - audio_seek → playback_seek (PLAYBACK_SEEK)
 * - audio_ended → playback_ended (PLAYBACK_ENDED)
 * - question_displayed → question_render (QUESTION_RENDER)
 * - question_answered → answer_select (ANSWER_SELECT)
 * - clip_completed → clip_complete (CLIP_COMPLETE)
 * - transcript_toggle → transcript_toggle (TRANSCRIPT_TOGGLE) [unchanged]
 * - transcript_sentence_replay_trigger → transcript_sentence_replay_trigger (TRANSCRIPT_SENTENCE_REPLAY_TRIGGER) [unchanged]
 * - transcript_sentence_replay_seek → transcript_sentence_replay_seek (TRANSCRIPT_SENTENCE_REPLAY_SEEK) [unchanged]
 * 
 * Legacy/transitional events kept for compatibility:
 * - app_init, clip_loaded, clip_started, transcript_autofollow_*, transcript_sentence_*, answer_card_state_applied
 * 
 * Total events migrated: 26 occurrences across the file
 * Migration completed with no functional logic changes
 */

import type { EventType } from '../types/events.js';

export const EVENT_TYPES: Record<string, EventType> = {
  // Playback events
  PLAYBACK_PLAY: 'playback_play',
  PLAYBACK_PAUSE: 'playback_pause',
  PLAYBACK_SEEK: 'playback_seek',
  PLAYBACK_REWIND_10: 'playback_rewind_10',
  PLAYBACK_FORWARD_10: 'playback_forward_10',
  PLAYBACK_ENDED: 'playback_ended',
  PLAYBACK_FIRST_LISTEN_COMPLETE: 'playback_first_listen_complete',
  
  // Transcript events
  TRANSCRIPT_TOGGLE: 'transcript_toggle',
  TRANSCRIPT_REVEAL: 'transcript_reveal',
  TRANSCRIPT_REPLAY_AFFORDANCE_MODE: 'transcript_replay_affordance_mode',
  TRANSCRIPT_SENTENCE_REPLAY_TRIGGER: 'transcript_sentence_replay_trigger',
  TRANSCRIPT_SENTENCE_REPLAY_SEEK: 'transcript_sentence_replay_seek',
  
  // Question and answer events
  QUESTION_RENDER: 'question_render',
  QUESTIONS_EARLY_START: 'questions_early_start',
  QUESTIONS_AUTO_REVEAL: 'questions_auto_reveal',
  ANSWER_SELECT: 'answer_select',
  ANSWER_RETRY: 'answer_retry',
  ANSWER_FINAL_INCORRECT: 'answer_final_incorrect',
  SEQUENCE_STEP_CORRECT: 'sequence_step_correct',
  SEQUENCE_STEP_INCORRECT: 'sequence_step_incorrect',
  SEQUENCE_COMPLETE: 'sequence_complete',
  
  // Clip lifecycle events
  CLIP_FINISH_CLICK: 'clip_finish_click',
  CLIP_COMPLETE: 'clip_complete',
  CLIP_METADATA_RENDER: 'clip_metadata_render',
  
  // Future placeholders (not yet implemented)
  SUMMARY_CHIP_VIEW: 'summary_chip_view',
  SUMMARY_CHIP_DISMISS: 'summary_chip_dismiss',
  AUTO_ADVANCE_TRIGGER: 'auto_advance_trigger',
  AUTO_ADVANCE_CANCEL: 'auto_advance_cancel',
  
  // Enhanced transcript auto-follow events (R8)
  TRANSCRIPT_FOLLOW_SCROLL: 'transcript_follow_scroll',
  TRANSCRIPT_FOLLOW_PAUSE_USER: 'transcript_follow_pause_user',
  TRANSCRIPT_FOLLOW_RESUME: 'transcript_follow_resume',
  
  // Legacy/transitional events (will be removed in future)
  APP_INIT: 'app_init',
  CLIP_LOADED: 'clip_loaded',
  CLIP_STARTED: 'clip_started',
  TRANSCRIPT_AUTOFOLLOW_SCROLL: 'transcript_autofollow_scroll',
  TRANSCRIPT_AUTOFOLLOW_RESUMED: 'transcript_autofollow_resumed',
  TRANSCRIPT_AUTOFOLLOW_PAUSED: 'transcript_autofollow_paused',
  TRANSCRIPT_SENTENCE_KEYBOARD_FOCUS: 'transcript_sentence_keyboard_focus',
  TRANSCRIPT_SENTENCE_TEXT_CLICKED: 'transcript_sentence_text_clicked',
  ANSWER_CARD_STATE_APPLIED: 'answer_card_state_applied',
  
  // CSS reduction events
  STYLE_TOKEN_MIGRATION_REPORT: 'style_token_migration_report',
  
  // Depth metrics events
  DEPTH_PASS_START: 'depth_pass_start',
  DEPTH_PASS_COMPLETE: 'depth_pass_complete',
  EARLY_REPLAY_DETECTED: 'early_replay_detected',
  
  // Experiment/feedback events
  DIFFICULTY_FEEDBACK: 'difficulty_feedback',
  REPLAY_ACTION: 'replay_action',
  
  // Daily goal events
  DAILY_GOAL_SHOWN: 'daily_goal_shown',
  DAILY_GOAL_STARTED: 'daily_goal_started',
  DAILY_GOAL_COMPLETED: 'daily_goal_completed',
  DAILY_GOAL_CLIP_COMPLETED: 'daily_goal_clip_completed',
  DAILY_GOAL_PROGRESS_UPDATED: 'daily_goal_progress_updated',
  DAILY_GOAL_GENERATED: 'daily_goal_generated',
  DAILY_GOAL_RESUMED: 'daily_goal_resumed',
  
  // Library and filtering events
  LIBRARY_FILTER_APPLIED: 'library_filter_applied',
  LIBRARY_SEARCH_APPLIED: 'library_search_applied',
  SERIES_VIEW_OPENED: 'series_view_opened',
  
  // Quality events
  REPORT_ISSUE: 'report_issue',
  REPORT_ISSUE_COPIED: 'report_issue_copied',
  
  // UI interaction events
  UI_PROGRESSBAR_RENDER: 'ui_progressbar_render',
  PLAYBACK_TIME_TICK: 'playback_time_tick',
  TRANSCRIPT_TOGGLE_SHOW: 'transcript_toggle_show',
  TRANSCRIPT_TOGGLE_HIDE: 'transcript_toggle_hide',
  REPORT_ISSUE_OPEN: 'report_issue_open',
  
  // Onboarding and preferences events
  PREFERENCE_DIFFICULTY_SET: 'preference_difficulty_set',
  ONBOARDING_SHOWN: 'onboarding_shown',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  
  // App lifecycle events
  APP_OPEN: 'app_open',
  
  // Series progress events
  SERIES_PROGRESS_UPDATED: 'series_progress_updated',
  
  // Auth events
  AUTH_SIGN_IN_REQUESTED: 'auth_sign_in_requested',
  AUTH_SIGN_IN: 'auth_sign_in',
  AUTH_SIGN_UP: 'auth_sign_up',
  AUTH_SIGN_OUT: 'auth_sign_out',
  AUTH_PASSWORD_RESET_REQUESTED: 'auth_password_reset_requested',
  AUTH_PASSWORD_RESET_COMPLETED: 'auth_password_reset_completed',
  AUTH_FORGOT_PASSWORD: 'auth_forgot_password',
  
  // UI interaction events (generic)
  UI_INTERACTION: 'ui_interaction',
  
  // Progress tracking events
  PROGRESS_UPDATE: 'progress_update',
  
  // Clip change events
  CLIP_CHANGE: 'clip_change'
} as const;

/**
 * Instrumentation Notes:
 * - Use logEvent(EVENT_TYPES.DOMAIN_ACTION, payload) for new events.
 * - Prefer extending existing event payloads over creating near-duplicate types.
 * - Avoid high-frequency logs (e.g., timeupdate) to keep buffer meaningful.
 * - Enable developer export via window.__listenUpEnableDebugEvents().
 */
